import {fromSeconds} from 'common/date';
import {ChevronRight} from 'lucide-react';
import {DateTime} from 'luxon';
import * as React from 'react';
import {Link} from 'react-router-dom';
import {CardArt} from 'src/CardArt';
import {
  formatCents,
  formatCentsShort,
  formatNetShort,
  inclusiveEndDay,
} from 'src/CardBenefitsView/benefitUtils';
import {nettedSpans, summarizeFeeYear} from 'src/CardBenefitsView/benefitWindows';
import {isCardProductId} from 'src/cardProductOptions';
import {AccountV2Icon} from 'src/icons/AccountV2Icon';
import {logoUrl} from 'src/logoOptions';
import type {FullFeePeriodFragment} from '@transactions/graphql';
import type {CardGroup} from 'src/BenefitsOverviewView/groupFeePeriodsByCard';
import type {FeeYearStats} from 'src/CardBenefitsView/benefitWindows';

const formatDay = (dt: DateTime): string =>
  dt.toLocaleString({month: 'short', day: 'numeric', year: 'numeric'});

// The card's face when it has one (a 34px chip is enough to tell a silver
// slab from a navy one), else the institution logo as before.
//
// The face follows the fee year the row is labelled with, not the account's
// current identity: the row name comes from the display period's product, so
// a lapsed Sapphire Reserve year on a card since converted to JetBlue must
// not put the JetBlue face beside the Reserve name. Falls back to the
// account's product for a period recorded before faces existed.
const CardLogo = ({feePeriod}: {feePeriod: FullFeePeriodFragment}) => {
  const {account} = feePeriod;
  const product = feePeriod.cardProduct ?? account.cardProduct;
  return isCardProductId(product) ? (
    <CardArt product={product} className="w-12" />
  ) : account.logo ? (
    <img
      src={logoUrl(account.logo)}
      alt=""
      className="size-5 shrink-0 rounded"
    />
  ) : (
    <AccountV2Icon account={account} className="size-5 shrink-0" />
  );
};

const NetLabel = ({net}: {net: number}) => (
  <span
    className={`text-sm font-medium tabular-nums ${
      net >= 0 ? 'text-positive' : 'text-destructive'
    }`}
  >
    {formatNetShort(net)}
  </span>
);

/**
 * Cycle meter against the year's potential, fee position as a tick: bar past
 * the tick means the tracked credits already outran the fee.
 */
const CycleMeter = ({stats}: {stats: FeeYearStats}) => {
  if (stats.potential <= 0) {
    return <div className="bg-muted/40 h-2 rounded-full" />;
  }
  // Scale to whichever is larger: when the fee exceeds the tracked potential
  // (e.g. a card whose value is mostly untracked perks), a full bar must
  // still stop short of the tick instead of pretending the fee was covered
  const scale = Math.max(stats.potential, stats.fee);
  const pct = (cents: number): number =>
    Math.max(0, Math.min(100, (cents / scale) * 100));
  const spans = nettedSpans(stats);
  return (
    <div
      className="relative"
      title={`${formatCents(Math.max(0, stats.captured))} of ${formatCents(stats.potential)} captured · fee ${formatCents(stats.fee)}`}
    >
      <div className="bg-muted/40 flex h-2 overflow-hidden rounded-full">
        <span className="bg-green-500/50" style={{width: `${pct(spans.posted)}%`}} />
        <span className="stripe-booked" style={{width: `${pct(spans.booked)}%`}} />
        <span className="bg-blue-400/60" style={{width: `${pct(stats.open)}%`}} />
        <span className="flex-1" />
        <span className="stripe-missed" style={{width: `${pct(stats.missed)}%`}} />
      </div>
      <span
        className="bg-foreground/70 absolute -inset-y-0.5 w-[2px] rounded-full"
        style={{left: `${pct(stats.fee)}%`}}
        title={`fee ${formatCents(stats.fee)}`}
      />
    </div>
  );
};

type Props = {
  group: CardGroup;
};

/**
 * One card, one row: the current cycle's meter with the fee tick, what's
 * open, and the running net. Lapsed and upcoming cards collapse to a quiet
 * footnote. The row links to the card's manage page.
 */
export const CardSection = ({group}: Props) => {
  const {displayPeriod} = group;
  const now = DateTime.now();

  if (!group.isCurrent) {
    const endDay = inclusiveEndDay(displayPeriod.endDate);
    // A recently-lapsed card reads as a result; long-dormant ones as history
    const recentlyEnded =
      !group.isUpcoming && now.diff(endDay, 'days').days <= 400;
    const stats = summarizeFeeYear(displayPeriod, now);
    const yearCount = 1 + group.pastPeriods.length;
    return (
      <Link
        to={`/accounts/${group.accountId}/benefits`}
        className="hover:bg-accent/20 flex items-center gap-2.5 px-4 py-2.5"
      >
        <CardLogo feePeriod={displayPeriod} />
        <span className="text-muted-foreground min-w-0 flex-1 truncate text-sm">
          <span className="text-foreground/80 font-medium">{group.name}</span>
          {group.isUpcoming ? (
            <> · starts {formatDay(fromSeconds(displayPeriod.startDate))}</>
          ) : recentlyEnded ? (
            // A custom window can stay open past the fee period's end — the
            // result isn't final until nothing is left to claim
            <>
              {' '}
              · ended {formatDay(endDay)}
              {stats.open > 0 ? '' : ' — final'}
            </>
          ) : (
            <>
              {' '}
              · no active fee period · {yearCount} fee year
              {yearCount === 1 ? '' : 's'}
            </>
          )}
        </span>
        {group.isUpcoming ? (
          <span className="text-muted-foreground text-xs tabular-nums">
            {formatCents(displayPeriod.amount)} fee
          </span>
        ) : stats.open > 0 ? (
          <span className="text-xs font-medium text-blue-600 tabular-nums dark:text-blue-300">
            {formatCentsShort(stats.open)} open
          </span>
        ) : recentlyEnded ? (
          <NetLabel net={stats.net} />
        ) : (
          <span className="text-muted-foreground text-xs">history</span>
        )}
        <ChevronRight className="text-muted-foreground size-3.5 shrink-0" />
      </Link>
    );
  }

  const stats = summarizeFeeYear(displayPeriod, now);
  return (
    <Link
      to={`/accounts/${group.accountId}/benefits`}
      // Narrow screens drop the meter and open columns (name + net only) so
      // the overview never scrolls horizontally
      className="hover:bg-accent/20 grid grid-cols-[minmax(0,1fr)_5.5rem_auto] items-center gap-3 px-4 py-2.5 lg:grid-cols-[minmax(11rem,15rem)_1fr_9.5rem_5.5rem_auto]"
    >
      <span className="flex min-w-0 items-center gap-2.5">
        <CardLogo feePeriod={displayPeriod} />
        <span className="min-w-0">
          <span className="block truncate text-sm font-medium">
            {group.name}
          </span>
          <span className="text-muted-foreground block truncate text-[11px]">
            to {formatDay(inclusiveEndDay(displayPeriod.endDate))} ·{' '}
            {formatCentsShort(displayPeriod.amount)} fee
          </span>
        </span>
      </span>
      <div className="hidden lg:block">
        <CycleMeter stats={stats} />
      </div>
      <span className="hidden text-right text-xs tabular-nums lg:block">
        {stats.open > 0 ? (
          <span className="text-blue-600 dark:text-blue-300">
            {stats.openWindowCount} open · {formatCentsShort(stats.open)}
          </span>
        ) : (
          <span className="text-muted-foreground">nothing open</span>
        )}
        {stats.missed > 0 && (
          <span className="text-amber-600 dark:text-amber-400">
            {' '}
            · −{formatCentsShort(stats.missed)}
          </span>
        )}
      </span>
      <span className="text-right">
        <NetLabel net={stats.net} />
      </span>
      <ChevronRight className="text-muted-foreground size-3.5 shrink-0" />
    </Link>
  );
};
